import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Button } from '../Button';

import type { AlertProps, AlertVariant } from './Alert.types';

/**
 * Variant-specific styling configuration
 * Based on design system: section 5.5 Feedback Components (Alerts / Banners)
 */
const variantStyles: Record<
  AlertVariant,
  {
    container: string;
    icon: string;
    title: string;
    content: string;
  }
> = {
  success: {
    container: 'bg-success-wash border-success-500/30',
    icon: 'text-success-500',
    title: 'text-success-500',
    content: 'text-success-500',
  },
  warning: {
    container: 'bg-warning-wash border-warning-500/30',
    icon: 'text-warning-500',
    title: 'text-warning-500',
    content: 'text-warning-500',
  },
  error: {
    container: 'bg-error-wash border-error-500/30',
    icon: 'text-error-500',
    title: 'text-error-500',
    content: 'text-error-500',
  },
  info: {
    container: 'bg-info-wash border-info-500/30',
    icon: 'text-info-500',
    title: 'text-info-500',
    content: 'text-info-500',
  },
  neutral: {
    container: 'bg-contrast-700 border-stroke-default',
    // Secondary text (neutral-400, 7.2:1 on the card): neutral-500 read at
    // 3.8:1, below AA for text. The icon stays muted; 3:1 is enough for it.
    icon: 'text-neutral-500',
    title: 'text-neutral-400',
    content: 'text-neutral-400',
  },
};

/**
 * Default icons for each variant
 */
const defaultIcons: Record<AlertVariant, React.ReactElement> = {
  success: <CheckCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  error: <XCircleIcon className="h-5 w-5" />,
  info: <InformationCircleIcon className="h-5 w-5" />,
  neutral: <InformationCircleIcon className="h-5 w-5" />,
};

/**
 * Alert component for displaying contextual feedback messages
 *
 * @example
 * ```tsx
 * // Success alert
 * <Alert variant="success" title="Success!">
 *   Your changes have been saved successfully.
 * </Alert>
 *
 * // Dismissible error alert
 * <Alert variant="error" dismissible onDismiss={() => setShow(false)}>
 *   Something went wrong. Please try again.
 * </Alert>
 *
 * // Error alert with a retry
 * <Alert variant="error" title="Couldn't load" action={{ label: 'Try again', onClick: refetch }}>
 *   {error}
 * </Alert>
 *
 * // Info alert with custom icon
 * <Alert variant="info" icon={<CustomIcon />}>
 *   This is an informational message.
 * </Alert>
 * ```
 */
export function Alert({
  variant = 'info',
  title,
  children,
  icon,
  hideIcon = false,
  dismissible = false,
  onDismiss,
  action,
  className = '',
  ...props
}: AlertProps) {
  const styles = variantStyles[variant];
  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div
      role="alert"
      className={`
        flex items-start gap-3 p-4 rounded-lg border
        ${styles.container}
        ${className}
      `.trim()}
      {...props}
    >
      {/* Icon */}
      {!hideIcon && (
        <div className={`flex-shrink-0 ${styles.icon}`} aria-hidden="true">
          {displayIcon}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className={`text-body-sm font-medium ${styles.title}`}>
            {title}
          </h3>
        )}
        <div
          className={`text-body-sm ${styles.content} ${title ? 'mt-1' : ''}`}
        >
          {children}
        </div>
        {action && (
          <Button
            variant="contrast"
            size="sm"
            onClick={action.onClick}
            leftIcon={action.leftIcon}
            loading={action.loading}
            disabled={action.disabled}
            className="mt-3"
          >
            {action.label}
          </Button>
        )}
      </div>

      {/* Dismiss button */}
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className={`
            flex-shrink-0 p-1 -m-1 rounded-md
            ${styles.icon}
            hover:bg-lift-3
            focus:outline-none focus-visible:ring-state-focus
            transition-colors duration-fast
          `}
          aria-label="Dismiss alert"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export default Alert;
